import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, OnInit, signal, ViewChild } from '@angular/core';
import { IconModule } from '@library/ui/atoms/icon/icon.module';
import { PipesModule } from '@library/utils/pipes/pipes.module';
import { WeathermapFacade } from '@store/weathermap.facade';
import { EventManagerService, EventSource } from 'src/app/services/event-manager.service';
import { LoginService } from 'src/app/services/login.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { DashboardComponent } from 'src/app/toolbar/dashboard/dashboard.component';
import { FiltersComponent } from 'src/app/toolbar/filters/filters.component';
import { GeolocateComponent } from 'src/app/toolbar/geolocate/geolocate.component';
import { LoginComponent } from 'src/app/toolbar/login/login.component';
import { SettingsComponent } from 'src/app/toolbar/settings/settings.component';
import { ToolbarItemComponent } from 'src/app/toolbar/toolbar-item/toolbar-item.component';
import { ToolbarPanelComponent } from 'src/app/toolbar/toolbar-panel/toolbar-panel.component';
import { ZoomComponent } from 'src/app/toolbar/zoom/zoom.component';

enum Panel {
  DASHBOARD = 'dashboard',
  SETTINGS = 'settings',
  FILTERS = 'filters',
}

@Component({
  selector: 'app-toolbar',
  imports: [
    IconModule,
    DashboardComponent,
    LoginComponent,
    SettingsComponent,
    GeolocateComponent,
    FiltersComponent,
    ToolbarItemComponent,
    ZoomComponent,
    CommonModule,
    PipesModule,
    ToolbarPanelComponent,
  ],
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      state('closed', style({
        transform: 'translateX(calc(-100% - 32px))',
      })),
      state('opened', style({
        transform: 'translateY(0)',
      })),
      transition('closed <=> opened', animate('150ms ease-in-out')),
    ]),
    trigger('enterLeave', [
      transition(':enter', [
        style({
          transform: 'translateX(calc(-100% - 32px))',
          opacity: 0,
        }),
        animate('150ms ease-in-out', style({
          transform: 'translateY(0)',
          opacity: 1,
        })),
      ]),
      transition(':leave', [
        animate('150ms ease-in-out', style({
          transform: 'translateX(calc(-100% - 32px))',
          opacity: 0,
        })),
      ]),
    ]),
  ],
})
export class ToolbarComponent implements OnInit {
  Panel = Panel;

  selectedPanel = signal(null);

  loggedIn = this.loginService.loggedIn;

  selectedStation = this.weathermapFacade.selectedStation;

  state: 'opened' | 'closed';

  outdoorModules = this.userDataService.outdoorModules;

  @HostBinding('@slideInOut') get slideInOut(): string {
    return this.selectedStation() ? 'closed' : 'opened';
  }

  @ViewChild('dashboard') dashboard: ElementRef<HTMLElement>;

  constructor(
    private readonly loginService: LoginService,
    private readonly weathermapFacade: WeathermapFacade,
    private readonly eventManagerService: EventManagerService,
    private readonly userDataService: UserDataService,
  ) {}

  ngOnInit(): void {
    this.eventManagerService.registerSource(EventSource.TOOLBAR, () => {
      this.closePanel();
    });
  }

  clickToolbarItem(panel: Panel, event: MouseEvent): void {
    this.selectPanel(panel);
    this.eventManagerService.closeOtherSources(EventSource.TOOLBAR);
    event.stopPropagation();
  }

  selectPanel(panel: Panel): void {
    if (panel === this.selectedPanel()) {
      this.closePanel();
    } else {
      this.selectedPanel.set(panel);
    }
  }

  closePanel(): void {
    this.selectedPanel.set(null);
  }

  openAxeptioCookies(): void {
    window.openAxeptioCookies();
  }
}
